/**
 * Safely divide two numbers. Returns default value if denominator is zero or invalid.
 */
export function safe_div(numerator: number, denominator: number, defaultValue: number = 0): number {
  if (typeof numerator !== "number" || typeof denominator !== "number" || isNaN(numerator) || isNaN(denominator)) {
    return defaultValue;
  }
  if (Math.abs(denominator) < 1e-9) {
    return defaultValue;
  }
  return numerator / denominator;
}

/**
 * Bounds checking and rounding helpers for ratios
 */
export function roundTo(num: number, decimals: number = 3): number {
  if (typeof num !== "number" || isNaN(num)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round((num + Number.EPSILON) * factor) / factor;
}

/**
 * Formats a value safely with spacer separators between groups of three digits
 */
export function formatRubles(val: number | null | undefined): string {
  if (val === null || val === undefined) return '—';
  if (typeof val !== 'number' || isNaN(val)) return '0 ₽';
  return `${Math.round(val).toLocaleString('ru-RU')} ₽`;
}

/** Разделители тысяч (пробел) для отображения при ВВОДЕ финансовых значений.
 * Сохраняет ведущий минус и десятичную часть; на вход — «сырая» строка (цифры/-/.). */
/** Сумма в рублях, округлённая до копеек, строкой для денежного поля: 1250.5 → "1250.5". */
export function toKopecksStr(n: number): string {
  return String(Math.round(n * 100) / 100);
}

export function groupThousands(s: string): string {
  if (s === '' || s === '-') return s;
  const neg = s.startsWith('-');
  const body = s.replace(/[^\d.]/g, '');
  const dot = body.indexOf('.');
  const intPart = dot >= 0 ? body.slice(0, dot) : body;
  const decPart = dot >= 0 ? body.slice(dot + 1) : '';
  const gi = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return (neg ? '-' : '') + gi + (dot >= 0 ? '.' + decPart : '');
}

/**
 * Разбор строки `riskCheckScore` в формате "X.X/10 (Метка)" — то, что
 * присылает бэк (sb_score.compute_sb_reliability).
 *
 * Возвращает { short: "X.X/10", label: "Метка", percentage: int }.
 * Если строка пустая или невалидна, поля = "—" / 0.
 */
export function parseRiskScore(raw: string | undefined | null): {
  short: string;
  label: string;
  percentage: number;
} {
  if (!raw) return { short: "—", label: "—", percentage: 0 };
  const short = raw.split(" ")[0] || "—";

  const open = raw.indexOf("(");
  const close = raw.lastIndexOf(")");
  const label = open >= 0 && close > open ? raw.substring(open + 1, close) : "—";

  const match = raw.match(/^([\d.]+)\/(\d+)/);
  const percentage = match
    ? Math.round((parseFloat(match[1]) / parseFloat(match[2])) * 100) || 0
    : 0;

  return { short, label, percentage };
}
